import { campusSchema, type CampusInput } from '@church/domain'
import {
  useCampuses,
  useCreateCampus,
  useDeleteCampus,
  useAccess,
  useUpdateCampus,
} from '@church/data'
import { faFloppyDisk, faLocationDot, faPlus, faTrashCan } from '@church/ui-icons'
import { zodResolver } from '@hookform/resolvers/zod'
import { createFileRoute } from '@tanstack/react-router'
import { useId } from 'react'
import { useForm } from 'react-hook-form'
import {
  ListCard,
  ListRow,
  ListSection,
  NewButton,
  usePanel,
} from '../components/platform/SettingsList.tsx'
import {
  Button,
  Card,
  ConfirmButton,
  EmptyState,
  ErrorNote,
  Field,
  Input,
  SidePanel,
  Spinner,
} from '../components/ui.tsx'

export const Route = createFileRoute('/_authed/_shell/settings/campuses')({
  component: CampusesTab,
})

function CampusesTab() {
  const { data: campuses, isPending } = useCampuses()
  const access = useAccess()
  const panel = usePanel<string>()
  const campus = campuses?.find((c) => c.id === panel.shown)
  const remove = useDeleteCampus()
  const create = useCreateCampus()
  const update = useUpdateCampus()
  const editing = campus !== undefined && panel.shown !== 'new'
  const formId = useId()

  return (
    <div className="max-w-2xl">
      <ListSection
        title="Campuses"
        icon={faLocationDot}
        hint="Where services happen. Staff can be limited to one, and lists filter by them."
        action={
          access.has('org', 'editor', { orgWide: true }) && (
            <NewButton label="New campus" onClick={() => panel.show('new')} />
          )
        }
      >
        {isPending ? (
          <Spinner />
        ) : !campuses || campuses.length === 0 ? (
          <Card>
            <EmptyState
              title="No campuses yet"
              hint="Single-site churches don't need any. Add campuses when services happen in more than one place."
            />
          </Card>
        ) : (
          <ListCard>
            {campuses.map((c) => (
              <ListRow
                key={c.id}
                title={c.name}
                meta={c.address ?? undefined}
                onOpen={
                  access.has('org', 'editor', { orgWide: true })
                    ? () => panel.show(c.id)
                    : undefined
                }
              />
            ))}
          </ListCard>
        )}
      </ListSection>

      <SidePanel
        open={panel.open && (panel.shown === 'new' || campus !== undefined)}
        onOpenChange={(next) => {
          if (!next) {
            create.reset()
            update.reset()
          }
          panel.onOpenChange(next)
        }}
        title={campus && panel.shown !== 'new' ? campus.name : 'New campus'}
        primary={
          <Button
            type="submit"
            form={formId}
            icon={editing ? faFloppyDisk : faPlus}
            className="py-1.5"
            pending={(editing ? update : create).isPending}
            pendingLabel="Saving…"
          >
            {editing ? 'Save' : 'Add'}
          </Button>
        }
        footer={
          campus && panel.shown !== 'new' ? (
            <ConfirmButton
              label="Remove campus"
              icon={faTrashCan}
              variant="quietDanger"
              className="-ml-4"
              disabled={remove.isPending}
              title={`Remove ${campus.name}?`}
              description="People assigned to it keep their records; the campus stops appearing in filters."
              confirmLabel="Remove campus"
              onConfirm={() => remove.mutate(campus.id, { onSuccess: panel.close })}
            />
          ) : undefined
        }
      >
        {panel.shown === 'new' ? (
          <CampusForm
            key="new"
            formId={formId}
            create={create}
            update={update}
            onDone={panel.close}
          />
        ) : (
          campus && (
            <CampusForm
              key={campus.id}
              formId={formId}
              create={create}
              update={update}
              defaults={{ name: campus.name, address: campus.address ?? undefined }}
              campusId={campus.id}
              onDone={panel.close}
            />
          )
        )}
        <ErrorNote error={remove.error} />
      </SidePanel>
    </div>
  )
}

function CampusForm({
  formId,
  create,
  update,
  defaults,
  campusId,
  onDone,
}: {
  formId: string
  create: ReturnType<typeof useCreateCampus>
  update: ReturnType<typeof useUpdateCampus>
  defaults?: Partial<CampusInput>
  campusId?: string
  onDone: () => void
}) {
  const mutation = campusId ? update : create

  const form = useForm<CampusInput>({
    resolver: zodResolver(campusSchema),
    defaultValues: defaults,
  })

  const submit = form.handleSubmit(async (input) => {
    if (campusId) await update.mutateAsync({ id: campusId, ...input })
    else await create.mutateAsync(input)
    form.reset()
    onDone()
  })

  return (
    <form id={formId} onSubmit={(e) => void submit(e)} className="space-y-5" noValidate>
      <Field label="Name" error={form.formState.errors.name?.message}>
        <Input placeholder="North Campus" {...form.register('name')} />
      </Field>
      <Field label="Address" error={form.formState.errors.address?.message}>
        <Input placeholder="Optional" {...form.register('address')} />
      </Field>

      <ErrorNote error={mutation.error} />
    </form>
  )
}
